import { useEffect, useMemo, useRef, useState } from 'react';
import { Surface } from '@cloudflare/kumo/components/surface';
import { Button } from '@cloudflare/kumo/components/button';
import { Banner } from '@cloudflare/kumo/components/banner';
import { Loader } from '@cloudflare/kumo/components/loader';
import { Empty } from '@cloudflare/kumo/components/empty';
import { Table } from '@cloudflare/kumo/components/table';
import { Badge } from '@cloudflare/kumo/components/badge';
import { Textarea } from '@cloudflare/kumo/components/input';
import { ArrowCounterClockwise, UploadSimple, DownloadSimple } from '@phosphor-icons/react';
import { api } from '@/lib/api';

// ブース割当の CSV 一括反映画面。
// 配置作業は CAD 図面 + 紙でやる (memory: project_2026_booth_assignment_csv)
// CSV フォーマット:
//   1 列目 applicationNumber (整数)
//   2 列目 boothNumber (例: A-12)
//   ヘッダ行は任意。空行はスキップ。同一 applicationNumber を複数行書くと
//   サーバ側で comma-join され multi-booth 扱いになる。

interface Assignment {
  id: string;
  applicationNumber: number;
  exhibitorName: string;
  exhibitorTitle: string;
  boothNumber: string | null;
  status: string;
}

interface ParsedRow {
  line: number;
  applicationNumber: number | null;
  boothNumber: string | null;
  error?: string;
}

export default function BoothsPage() {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [csvText, setCsvText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ data: Assignment[] }>('/api/admin/booth-assignments');
      setAssignments(res.data.data ?? []);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const parsed = useMemo(() => parseCsv(csvText), [csvText]);
  const validRows = parsed.filter((r) => !r.error && r.applicationNumber !== null && r.boothNumber);
  const errorRows = parsed.filter((r) => r.error);

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      // BOM 付き UTF-8 を吸収
      setCsvText(text.replace(/^﻿/, ''));
    };
    reader.readAsText(file, 'utf-8');
  };

  const submit = async () => {
    if (validRows.length === 0) return;
    setSubmitting(true);
    setError(null);
    setInfo(null);
    try {
      // 同一 applicationNumber の複数行を集約 (multi-booth 用)
      const map = new Map<number, string[]>();
      for (const r of validRows) {
        const list = map.get(r.applicationNumber!) ?? [];
        list.push(r.boothNumber!);
        map.set(r.applicationNumber!, list);
      }
      const payload = {
        assignments: Array.from(map.entries()).map(([applicationNumber, boothNumbers]) => ({
          applicationNumber,
          boothNumbers,
        })),
      };
      const res = await api.post('/api/admin/booth-assignments/bulk', payload);
      const data = (res.data?.data ?? res.data) as {
        successCount: number;
        errorCount: number;
        errors?: string[];
      };
      setInfo(
        `${data.successCount} 件を反映しました${
          data.errorCount > 0 ? ` (エラー ${data.errorCount} 件)` : ''
        }`
      );
      if (data.errors && data.errors.length > 0) {
        setError(data.errors.slice(0, 5).join(' / '));
      }
      setCsvText('');
      if (fileRef.current) fileRef.current.value = '';
      await refresh();
    } catch (e) {
      setError(extractError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const downloadCurrentAsCsv = () => {
    const header = 'applicationNumber,boothNumber\n';
    const rows = assignments
      .filter((a) => a.boothNumber)
      .flatMap((a) =>
        (a.boothNumber as string)
          .split(',')
          .map((b) => `${a.applicationNumber},${b.trim()}`)
      );
    const csv = '﻿' + header + rows.join('\n') + '\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `booth-assignments-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-kumo-default">ブース割当</h1>
          <p className="text-sm text-kumo-muted">
            配置は CAD 図面 + 紙で詰めて、出展者番号と配置番号の組を CSV でアップロードします。
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            icon={DownloadSimple}
            onClick={downloadCurrentAsCsv}
            disabled={assignments.length === 0}
          >
            現状を CSV ダウンロード
          </Button>
          <Button
            variant="ghost"
            icon={ArrowCounterClockwise}
            loading={loading}
            onClick={refresh}
          >
            再読込
          </Button>
        </div>
      </div>

      {error && <Banner variant="error">{error}</Banner>}
      {info && <Banner variant="default">{info}</Banner>}

      <Surface className="p-4">
        <h2 className="mb-2 text-base font-semibold text-kumo-default">CSV を一括反映</h2>
        <p className="mb-3 text-xs text-kumo-muted">
          1 列目に出展者番号 (applicationNumber)、2 列目にブース番号。ヘッダ行は任意。
          同一の出展者番号を複数行書くと multi-booth 扱いになります。例:
          <code className="ml-2 rounded bg-kumo-base px-1 py-0.5">11,A-12</code>
          <br />
          ブース番号の末尾の「土」「日」(例: D-7土) は、土日で分け合うブースの印として受け付け、
          外して登録します (曜日は出展日で決まります)。
        </p>
        <div className="flex flex-col gap-3">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
            }}
            className="text-sm text-kumo-default"
          />
          <Textarea
            placeholder={'applicationNumber,boothNumber\n11,A-12\n12,A-13\n...'}
            value={csvText}
            onChange={(e) => setCsvText(e.currentTarget.value)}
            rows={6}
          />
          {parsed.length > 0 && (
            <div className="text-xs text-kumo-muted">
              {validRows.length} 行 OK / {errorRows.length} 行エラー
            </div>
          )}
          {errorRows.length > 0 && (
            <Banner variant="alert">
              <div className="flex flex-col gap-1 text-xs">
                {errorRows.slice(0, 5).map((r) => (
                  <span key={r.line}>
                    {r.line} 行目: {r.error}
                  </span>
                ))}
                {errorRows.length > 5 && (
                  <span className="text-kumo-muted">
                    ... 他 {errorRows.length - 5} 件
                  </span>
                )}
              </div>
            </Banner>
          )}
          <div>
            <Button
              variant="primary"
              icon={UploadSimple}
              disabled={validRows.length === 0 || submitting}
              loading={submitting}
              onClick={submit}
            >
              {validRows.length} 件を反映
            </Button>
          </div>
        </div>
      </Surface>

      <Surface>
        <div className="border-b border-kumo-border px-4 py-3">
          <h2 className="text-base font-semibold text-kumo-default">
            現在の割当 ({assignments.length})
          </h2>
        </div>
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader />
          </div>
        ) : assignments.length === 0 ? (
          <Empty title="割当済の出展者はいません" />
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.Head>申込#</Table.Head>
                <Table.Head>出展者</Table.Head>
                <Table.Head>出展タイトル</Table.Head>
                <Table.Head>ブース</Table.Head>
                <Table.Head>状態</Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {assignments.map((a) => (
                <Table.Row key={a.id}>
                  <Table.Cell>#{a.applicationNumber}</Table.Cell>
                  <Table.Cell>{a.exhibitorName}</Table.Cell>
                  <Table.Cell>{a.exhibitorTitle}</Table.Cell>
                  <Table.Cell>
                    <span className="font-mono">{a.boothNumber}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge variant="outline">{a.status}</Badge>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Surface>
    </div>
  );
}

// CSV をシンプルに parse。引用符付きセル / 改行入りセルは未対応 (配置用 CSV は単純な数値と
// "A-12" 形式しか含まない前提)。空行・ヘッダ行 (applicationNumber と書いた行) はスキップ。
function parseCsv(text: string): ParsedRow[] {
  const lines = text.split(/\r?\n/);
  const rows: ParsedRow[] = [];
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i].trim();
    if (!raw) continue;
    if (i === 0 && /^applicationNumber/i.test(raw)) continue; // header
    const cells = raw.split(',').map((s) => s.trim());
    if (cells.length < 2) {
      rows.push({ line: i + 1, applicationNumber: null, boothNumber: null, error: '列が足りません' });
      continue;
    }
    // 番号全体が数字のときだけ受け付ける (parseInt だと「11foo」を 11 と読み、別の出展者に割り当たる)
    const num = Number(cells[0]);
    if (!/^\d+$/.test(cells[0])) {
      rows.push({ line: i + 1, applicationNumber: null, boothNumber: null, error: `出展者番号が数値ではありません: ${cells[0]}` });
      continue;
    }
    const booth = cells[1];
    if (!booth) {
      rows.push({ line: i + 1, applicationNumber: num, boothNumber: null, error: 'ブース番号が空です' });
      continue;
    }
    rows.push({ line: i + 1, applicationNumber: num, boothNumber: booth });
  }
  return rows;
}

function extractError(e: unknown): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const r = (e as { response?: { data?: { error?: string; message?: string } } }).response;
    return r?.data?.error ?? r?.data?.message ?? '読み込みに失敗しました';
  }
  return e instanceof Error ? e.message : '読み込みに失敗しました';
}
